import { ArrowUpRight, Facebook, Linkedin, Mail, MapPin, Sparkles } from 'lucide-react';
import type { ReactNode } from 'react';
import { Container } from './Container';
import { ROUTES } from '@shared/constants/routes';
import { AUDIENCES } from '@shared/constants/audiences';
import { LEGAL } from '@shared/constants/legal-info';
import {
  getToolHref,
  getToolsForAudience,
  sortToolsByRelevance,
  type ToolId,
} from '@shared/constants/tools-catalog';
import { AUDIENCE_NAV } from '../../config/audience-nav';
import { useEffectiveAudience } from '../../hooks/useEffectiveAudience';
import { NewsletterInline } from '../conversion/NewsletterInline';
import { cn } from '../../lib/cn';

const FOOTER_TOOL_LABELS: Record<ToolId, string> = {
  'diagnostic-cee': 'Diagnostic CEE',
  'calculer-mes-aides': 'Aides rénovation',
  'comparateur-energie': 'Comparateur énergie',
  'decret-tertiaire': 'Décret Tertiaire',
  'cee-artisans': 'Primes artisans',
  'cee-catalogue': 'Catalogue CEE',
};

const COMPANY = [
  { label: 'À propos', href: ROUTES.aPropos },
  { label: 'Cas clients', href: ROUTES.casClients },
  { label: 'Blog & ressources', href: ROUTES.blog },
  { label: 'Tous les outils', href: ROUTES.outils },
  { label: 'Devenir partenaire', href: ROUTES.devenirPartenaire },
  { label: 'Contact', href: ROUTES.contact },
];

const LEGAL_LINKS = [
  { label: 'Mentions légales', href: ROUTES.legal.mentions },
  { label: 'Confidentialité', href: ROUTES.legal.confidentialite },
  { label: 'Cookies', href: ROUTES.legal.cookies },
  { label: 'CGV courtier', href: ROUTES.legal.cgv },
  { label: 'Accessibilité', href: ROUTES.legal.accessibilite },
];

export function Footer() {
  const year = new Date().getFullYear();

  // ───────────── Résolution du profil (hook unifié) ─────────────
  // Le Footer personnalise son contenu dès qu'un profil est actif,
  // que ce soit via l'URL (page audience) ou via le store (profil mémorisé).
  const { effective: activeAudience } = useEffectiveAudience();

  const audienceNav = activeAudience ? AUDIENCE_NAV[activeAudience.id] : null;
  const activeAudienceId = activeAudience?.id ?? null;
  const tools = sortToolsByRelevance(getToolsForAudience(activeAudienceId), activeAudienceId).map(
    (tool) => ({
      label: FOOTER_TOOL_LABELS[tool.id],
      href: getToolHref(tool.id, activeAudienceId),
    }),
  );

  return (
    <footer className="relative overflow-hidden border-t border-stone-200/70 bg-paper">
      {/* Halo décoratif émeraude — top */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-500/50 to-transparent"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -top-32 left-1/2 h-64 w-[64rem] -translate-x-1/2 rounded-full bg-brand-500/[0.06] blur-3xl"
      />

      {/* ─────────── BRAND + COLONNES ─────────── */}
      <Container className="relative pb-14 pt-14">
        <div className="grid gap-12 lg:grid-cols-[1.1fr,2.4fr,1.1fr]">
          {/* Brand */}
          <div>
            <a
              href="/"
              aria-label="Solution Éco France — accueil"
              className="group inline-flex items-center gap-2"
            >
              <img
                src="/sef-symbol.svg"
                alt=""
                aria-hidden
                width={32}
                height={35}
                className="h-8 w-auto shrink-0 transition-transform duration-200 ease-out group-hover:scale-105"
              />
              <span className="bg-gradient-to-br from-accent-800 via-accent-700 to-accent-600 bg-clip-text font-logo text-base font-bold uppercase tracking-[-0.01em] text-transparent">
                SOLUTION ÉCO FRANCE
              </span>
            </a>
            <p className="mt-5 max-w-xs text-[14px] leading-relaxed text-stone-600">
              Travaux de rénovation, comparaison de contrats d'énergie, primes CEE pour artisans. Un
              seul interlocuteur pour tous vos projets.
            </p>

            <ul className="mt-6 space-y-2.5 text-[12.5px] text-stone-600">
              <li className="inline-flex items-start gap-2">
                <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-stone-400" strokeWidth={1.8} />
                <span>
                  {LEGAL.headquarters.street}
                  <br />
                  {LEGAL.headquarters.postalCode} {LEGAL.headquarters.city}
                </span>
              </li>
              <li className="inline-flex items-center gap-2">
                <Mail className="h-3.5 w-3.5 shrink-0 text-stone-400" strokeWidth={1.8} />
                <a href="mailto:contact@solutionecofrance.fr" className="hover:text-ink">
                  contact@solutionecofrance.fr
                </a>
              </li>
            </ul>

            {/* Réseaux sociaux */}
            <div className="mt-5 flex items-center gap-2">
              <a
                href="https://www.linkedin.com/company/solution-eco-france"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-stone-200 bg-paper text-stone-600 transition-all duration-200 ease-out hover:-translate-y-0.5 hover:border-stone-300 hover:bg-stone-50 hover:text-ink"
              >
                <Linkedin className="h-4 w-4" strokeWidth={1.8} />
              </a>
              <a
                href="https://www.facebook.com/solutionecofrance"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-stone-200 bg-paper text-stone-600 transition-all duration-200 ease-out hover:-translate-y-0.5 hover:border-stone-300 hover:bg-stone-50 hover:text-ink"
              >
                <Facebook className="h-4 w-4" strokeWidth={1.8} />
              </a>
            </div>

            {/* Affiliation ATEE + Trustpilot */}
            <div className="mt-6 flex flex-wrap items-stretch gap-2">
              <a
                href="https://atee.fr"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Membre de l'ATEE — Association Technique Énergie Environnement"
                className="group inline-flex items-center gap-2.5 rounded-lg border border-stone-200/70 bg-paper px-2.5 py-2 transition-all duration-200 ease-out hover:-translate-y-0.5 hover:border-stone-300 hover:shadow-elevated"
              >
                <img
                  src="/atee-logo.png"
                  alt="Logo ATEE"
                  width={48}
                  height={28}
                  loading="lazy"
                  decoding="async"
                  className="h-7 w-auto shrink-0"
                />
                <span className="flex flex-col leading-tight">
                  <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-stone-400">
                    Membre de
                  </span>
                  <span className="text-[12.5px] font-semibold text-ink">l&rsquo;ATEE</span>
                </span>
              </a>

              <a
                href="https://fr.trustpilot.com/review/solutionecofrance.fr"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Voir les avis Solution Éco France sur Trustpilot — TrustScore 4.9 sur 5"
                className="group inline-flex flex-col items-center justify-center gap-0.5 rounded-lg border border-stone-200/70 bg-paper px-2.5 py-1.5 transition-all duration-200 ease-out hover:-translate-y-0.5 hover:border-stone-300 hover:shadow-elevated"
              >
                <img
                  src="/trustpilot-badge.png"
                  alt="Trustpilot — Avis vérifiés"
                  width={140}
                  height={70}
                  loading="lazy"
                  decoding="async"
                  className="h-7 w-auto"
                />
                <span className="text-[10.5px] font-medium text-stone-600">
                  <span className="font-semibold text-ink">TrustScore 4.9</span>
                  <span className="text-stone-400"> / 5</span>
                </span>
              </a>
            </div>
          </div>

          {/* 3 colonnes — adaptatif si audience */}
          <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-3">
            {/* Colonne 1 : raccourcis audience OU profils */}
            {audienceNav && activeAudience ? (
              <FooterColumn title={`Raccourcis ${activeAudience.shortLabel}`}>
                {audienceNav.quickLinks.map((link) => (
                  <FooterLink key={link.href} href={link.href}>
                    {link.label}
                  </FooterLink>
                ))}
              </FooterColumn>
            ) : (
              <FooterColumn title="Profils">
                {AUDIENCES.map((a) => (
                  <FooterLink key={a.id} href={a.href}>
                    {a.label}
                    {a.highlight && (
                      <span className="ml-1.5 inline-block h-1.5 w-1.5 rounded-full bg-accent-500" />
                    )}
                  </FooterLink>
                ))}
              </FooterColumn>
            )}

            <FooterColumn title="Outils">
              {tools.map((t) => (
                <FooterLink key={t.href} href={t.href}>
                  {t.label}
                </FooterLink>
              ))}
            </FooterColumn>

            <FooterColumn title="Société">
              {COMPANY.map((c) => (
                <FooterLink key={c.href} href={c.href}>
                  {c.label}
                </FooterLink>
              ))}
            </FooterColumn>
          </div>

          {/* Sidebar contact rapide — adaptatif */}
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-400">
              Contact rapide
            </p>
            <div className="mt-4 space-y-2">
              {(audienceNav?.footerCtas ?? DEFAULT_FOOTER_CTAS).map((cta) => {
                const isHighlight = cta.variant === 'highlight';
                const isPrimary = cta.variant === 'primary';
                return (
                  <a
                    key={cta.href + cta.label}
                    href={cta.href}
                    className={cn(
                      'group flex items-start justify-between gap-2 rounded-lg p-3 transition-all duration-200 ease-out hover:-translate-y-0.5 hover:shadow-elevated',
                      isHighlight
                        ? 'border border-accent-200/60 bg-accent-50/40 hover:border-accent-300'
                        : isPrimary
                          ? 'border border-stone-200/80 bg-paper hover:border-stone-300'
                          : 'border border-stone-200/70 bg-paper hover:border-stone-300',
                    )}
                  >
                    <div>
                      <p
                        className={cn(
                          'inline-flex items-center gap-1 text-[12.5px] font-semibold',
                          isHighlight ? 'text-accent-800' : 'text-ink',
                        )}
                      >
                        {isHighlight && <Sparkles className="h-3 w-3" />}
                        {cta.label}
                      </p>
                      <p
                        className={cn(
                          'mt-0.5 text-[11px]',
                          isHighlight ? 'text-accent-700/80' : 'text-stone-500',
                        )}
                      >
                        {cta.description}
                      </p>
                    </div>
                    <ArrowUpRight
                      className={cn(
                        'h-3.5 w-3.5 shrink-0 transition-all duration-150 ease-out group-hover:translate-x-0.5',
                        isHighlight
                          ? 'text-accent-600 group-hover:text-accent-800'
                          : 'text-stone-300 group-hover:text-stone-700',
                      )}
                      strokeWidth={2}
                    />
                  </a>
                );
              })}
              {/* Si on est dans une audience, on garde un lien permanent vers l'espace artisan
                  car c'est un parcours phare V3 (sauf si on EST déjà dans /artisans) */}
              {activeAudience && activeAudience.id !== 'artisans' && (
                <a
                  href={ROUTES.devenirPartenaire}
                  className="group flex items-start justify-between gap-2 rounded-lg border border-stone-200/60 bg-stone-50/40 p-3 text-[11px] transition-all duration-200 ease-out hover:-translate-y-0.5 hover:border-accent-300 hover:bg-accent-50/40"
                >
                  <div>
                    <p className="inline-flex items-center gap-1 text-[12px] font-semibold text-stone-600 group-hover:text-accent-800">
                      <Sparkles className="h-3 w-3" />
                      Vous êtes artisan RGE ?
                    </p>
                    <p className="mt-0.5 text-[11px] text-stone-500 group-hover:text-accent-700/80">
                      Primes énergie, zéro avance, paiement sous 60 jours
                    </p>
                  </div>
                  <ArrowUpRight
                    className="h-3.5 w-3.5 shrink-0 text-stone-300 transition-all duration-150 ease-out group-hover:translate-x-0.5 group-hover:text-accent-700"
                    strokeWidth={2}
                  />
                </a>
              )}
            </div>
          </div>
        </div>

        <div className="mt-14 border-t border-stone-200/70 pt-10">
          <NewsletterInline
            tone="panel"
            title="Recevez les actualités aides & primes"
            description="Évolutions MaPrimeRénov', primes CEE et échéances réglementaires — sans spam, désinscription en 1 clic."
            className="mx-auto max-w-2xl"
          />
        </div>

        {/* Bottom bar */}
        <div className="mt-10 flex flex-col-reverse items-start justify-between gap-4 border-t border-stone-200/70 pt-7 text-[12px] text-stone-500 md:flex-row md:items-center">
          <p>© {year} Solution Éco France</p>
          <ul className="flex flex-wrap items-center gap-x-5 gap-y-2 md:justify-end">
            {LEGAL_LINKS.map((l) => (
              <li key={l.href}>
                <a href={l.href} className="transition-colors duration-200 ease-out hover:text-ink">
                  {l.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </Container>
    </footer>
  );
}

const DEFAULT_FOOTER_CTAS = [
  {
    label: 'Estimer mes primes',
    href: ROUTES.diagnostic,
    description: 'Sans inscription, résultat immédiat',
    variant: 'primary' as const,
  },
  {
    label: 'Parler à un expert',
    href: ROUTES.contact,
    description: 'Réponse sous 24 h',
    variant: 'secondary' as const,
  },
  {
    label: 'Espace artisan',
    href: ROUTES.devenirPartenaire,
    description: 'Primes énergie, zéro avance',
    variant: 'highlight' as const,
  },
];

function FooterColumn({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-400">
        {title}
      </p>
      <ul className="mt-4 space-y-2.5">{children}</ul>
    </div>
  );
}

function FooterLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <li>
      <a
        href={href}
        className="group inline-flex items-center text-[14px] text-stone-600 transition-colors duration-200 ease-out hover:text-ink"
      >
        {children}
      </a>
    </li>
  );
}
